// Badge.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import StatefulImage from '../component/StatefulImage.jsx';

import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

import { color, textColor } from '../style/variables.js';

import DefaultBadge from '../../img/badge-default.png';
import LoadingImage from '../../img/loading.png';

const Badge = ({ title, icon }) => {
  const isStringTitle = typeof title === 'string';

  const titleChildren = title.props?.children;
  const isStringTitleChildren = typeof titleChildren === 'string';

  const imageAlt = isStringTitle
    ? title
    : isStringTitleChildren
    ? titleChildren
    : icon.props?.alt || '';

  const iconElement = useMemo(() => {
    const isStringIcon = typeof icon === 'string';
    if (!isStringIcon) {
      return icon;
    }

    const isPublicIcon = isStringIcon && icon.includes(env.PUBLIC_URL_PREFIX);
    if (isPublicIcon) {
      const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
        MediaAssetFormat.AVIF,
        MediaAssetFormat.WEBP,
        MediaAssetFormat.JPG,
      ].map(format => getPublicSrcSet({ href: icon, size: 64, format }));
      return (
        <StatefulImage>
          <ImageWrapper data-key="loading" width="68" height="68">
            <img src={LoadingImage} />
          </ImageWrapper>
          <ImageWrapper data-key="error" width="60" height="60">
            <img src={DefaultBadge} />
          </ImageWrapper>
          <ImageWrapper data-key="target" alt={imageAlt} width="60" height="60">
            <source {...avifSrcSet} type="image/avif" />
            <source {...webpSrcSet} type="image/webp" />
            <img {...jpgSrcSet} type="image/jpg" />
          </ImageWrapper>
        </StatefulImage>
      );
    }

    return (
      <StatefulImage>
        <ImageWrapper data-key="loading" width="68" height="68">
          <img src={LoadingImage} />
        </ImageWrapper>
        <ImageWrapper data-key="error" width="60" height="60">
          <img src={DefaultBadge} />
        </ImageWrapper>
        <ImageWrapper data-key="target" alt={imageAlt} width="60" height="60">
          <img src={icon} />
        </ImageWrapper>
      </StatefulImage>
    );
  }, [icon, imageAlt]);

  return (
    <StyledBadge>
      <Icon>{iconElement}</Icon>
      <Title>{title}</Title>
    </StyledBadge>
  );
};

Badge.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

Badge.defaultProps = {
  icon: '',
  title: '',
};

const StyledBadge = styled.div`
  display: inline-block;

  & > * {
    display: inline-block;
    vertical-align: middle;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Icon = styled.span`
  position: relative;
  z-index: 1;
  border: 1px solid ${color.white};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  box-sizing: content-box;
  display: inline-block;
  overflow: hidden;
`;

const ImageWrapper = styled.picture`
  & > img {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.span`
  margin-left: -20px;
  border-radius: 9px;
  padding-left: 22px;
  padding-right: 4px;
  height: 18px;
  line-height: 1;
  font-weight: 600;
  font-size: 11px;
  background-color: ${color.white};
  color: ${textColor.black};
`;

export default Badge;
